import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Parameters {
  buy_amount: number;
  profit_percent: number;
  auto_buy_up_interval: number;
  auto_buy_down_percent: number;
  fee_percent: number;
}

interface ParametersModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedBot: string;
  user: any;
  onParametersUpdated?: (params: Parameters) => void;
}

export const ParametersModal: React.FC<ParametersModalProps> = ({
  isOpen,
  onClose,
  selectedBot,
  user,
  onParametersUpdated
}) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language === 'ENG' ? 'ENG' : 'RUS';
  const [parameters, setParameters] = useState<Parameters | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);

  // Локальные значения для редактирования
  const [localParams, setLocalParams] = useState<Parameters | null>(null);
  // Состояния для валидации каждого поля
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  // Состояния для отображения значений в полях ввода
  const [inputValues, setInputValues] = useState<Record<string, string>>({});

  // Сбрасываем состояние при каждом открытии модального окна
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setUpdateError(null);
      fetchParameters();
    }
  }, [isOpen]);

  const fetchParameters = async () => {
    if (!user || !selectedBot) return;

    setLoading(true);
    setError(null);
    setValidationErrors({});
    setInputValues({});

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/trading_bot/get_params`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: parseInt(user.user_id),
          bot_name: selectedBot
        })
      });

      const result = await response.json();

      if (result.success) {
        const params = result.params;
        setParameters(params);
        setLocalParams(params);
        
        // Инициализируем inputValues с текущими значениями (только нужные параметры)
        const allowedParams = ['buy_amount', 'profit_percent', 'auto_buy_up_interval', 'auto_buy_down_percent', 'fee_percent'];
        const initialInputValues: Record<string, string> = {};
        Object.entries(params).forEach(([key, value]) => {
          if (allowedParams.includes(key)) {
            initialInputValues[key] = (value as number).toString();
          }
        });
        setInputValues(initialInputValues);
      } else {
        setError(result.error || 'Ошибка получения параметров');
      }
    } catch (err) {
      setError('Ошибка соединения с сервером');
      console.error('Error fetching parameters:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (param: keyof Parameters, value: string) => {
    if (!localParams) return;

    const newParams = { ...localParams };
    const newValidationErrors = { ...validationErrors };
    const newInputValues = { ...inputValues };

    // Обработка ввода: замена запятой на точку и фильтрация символов
    let processedValue = value;
    
    // Заменяем запятую на точку
    processedValue = processedValue.replace(',', '.');
    
    // Оставляем только цифры и точку
    processedValue = processedValue.replace(/[^0-9.]/g, '');
    
    // Убираем лишние точки (оставляем только первую)
    const parts = processedValue.split('.');
    if (parts.length > 2) {
      processedValue = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Ограничиваем количество знаков после запятой до 3
    if (parts.length === 2 && parts[1].length > 3) {
      processedValue = parts[0] + '.' + parts[1].substring(0, 3);
    }

    // Обновляем отображаемое значение
    newInputValues[param] = processedValue;

    // Если поле пустое, устанавливаем 0 и показываем ошибку
    if (processedValue === '' || processedValue === '-') {
      newParams[param] = 0;
      // Показываем ошибку для пустого поля
      newValidationErrors[param] = t('fieldRequired');
      setLocalParams(newParams);
      setValidationErrors(newValidationErrors);
      setInputValues(newInputValues);
      return;
    }

    // Парсим числовое значение
    const numValue = parseFloat(processedValue);

    if (param === 'auto_buy_up_interval') {
      // Для интервала используем целые числа
      newParams[param] = isNaN(numValue) ? 0 : Math.floor(numValue);
    } else {
      newParams[param] = isNaN(numValue) ? 0 : numValue;
    }

    // Убеждаемся, что значение является числом
    if (typeof newParams[param] !== 'number') {
      newParams[param] = 0;
    }

    // Валидация в реальном времени
    const error = validateParamValue(param, newParams[param]);
    if (error) {
      newValidationErrors[param] = error;
    } else {
      delete newValidationErrors[param];
    }

    setLocalParams(newParams);
    setValidationErrors(newValidationErrors);
    setInputValues(newInputValues);
  };

  const handleUpdateParameters = async () => {
    if (!localParams || !user || !selectedBot) return;

    // Проверяем, есть ли ошибки валидации
    if (Object.keys(validationErrors).length > 0) {
      return; // Просто выходим, не показываем дополнительных ошибок
    }

    setUpdating(true);
    setUpdateError(null);

    try {
      // Фильтруем только нужные параметры для отправки
      const allowedParams = ['buy_amount', 'profit_percent', 'auto_buy_up_interval', 'auto_buy_down_percent', 'fee_percent'];
      const filteredParams = Object.entries(localParams).filter(([param]) => allowedParams.includes(param));
      
      // Обновляем каждый параметр по отдельности
      const updatePromises = filteredParams.map(([param, value]) => {
        const requestBody = {
          user_id: parseInt(user.user_id),
          bot_name: selectedBot,
          param: param,
          value: typeof value === 'string' ? parseFloat(value) : value
        };
        
        // console.log(`Sending request for ${param}:`, requestBody);
        
        return fetch(`${process.env.REACT_APP_API_URL}/trading_bot/set_param`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        }).then(async res => {
          const result = await res.json();
          // console.log(`Response for ${param}:`, res.status, result);
          return result;
        });
      });

      const results = await Promise.all(updatePromises);
      const failedUpdates = results.filter(result => !result.success);

      if (failedUpdates.length === 0) {
        // Все параметры обновлены успешно
        setParameters(localParams);
        onParametersUpdated?.(localParams);
        onClose();
      } else {
        setUpdateError('Ошибка обновления некоторых параметров');
      }
    } catch (err) {
      setUpdateError('Ошибка соединения с сервером');
      console.error('Error updating parameters:', err);
    } finally {
      setUpdating(false);
    }
  };

  const handleResetParameters = async () => {
    if (!user || !selectedBot) return;

    setResetting(true);
    setUpdateError(null);
    setValidationErrors({});

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/trading_bot/reset_params`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: parseInt(user.user_id),
          bot_name: selectedBot
        })
      });

      const result = await response.json();

      if (result.success) {
        // Обновляем локальные параметры дефолтными значениями
        const defaultParams = result.params;
        setParameters(defaultParams);
        setLocalParams(defaultParams);
        
        // Обновляем inputValues с дефолтными значениями (только нужные параметры)
        const allowedParams = ['buy_amount', 'profit_percent', 'auto_buy_up_interval', 'auto_buy_down_percent', 'fee_percent'];
        const defaultInputValues: Record<string, string> = {};
        Object.entries(defaultParams).forEach(([key, value]) => {
          if (allowedParams.includes(key)) {
            defaultInputValues[key] = (value as number).toString();
          }
        });
        setInputValues(defaultInputValues);
        
        onParametersUpdated?.(defaultParams);
        onClose();
      } else {
        setUpdateError(result.error || 'Ошибка сброса параметров');
      }
    } catch (err) {
      setUpdateError('Ошибка соединения с сервером');
      console.error('Error resetting parameters:', err);
    } finally {
      setResetting(false);
    }
  };

  const formatValue = (value: number, param: keyof Parameters): string => {
    if (param === 'auto_buy_up_interval') {
      return Math.round(value).toString();
    }
    return value.toString();
  };

  const validateParamValue = (param: keyof Parameters, value: number): string | null => {
    if (param === "buy_amount") {
      if (value < 1) {
        return `${t('minValue')}: 1`;
      }
    } else if (param === "profit_percent") {
      if (value < 0.05) {
        return `${t('minValue')}: 0.05`;
      }
    } else if (param === "auto_buy_up_interval") {
      if (value < 1 || value > 300) {
        return `${t('valueRange')} 1 ${t('to')} 300`;
      }
    } else if (param === "auto_buy_down_percent") {
      if (value < 0.05) {
        return `${t('minValue')}: 0.05`;
      }
    } else if (param === "fee_percent") {
      if (value < 0.001 || value > 10) {
        return `${t('valueRange')} 0.001 ${t('to')} 10`;
      }
    }
    return null;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-header border border-border rounded-lg w-full max-w-md max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="pl-2 pr-1 py-2 border-b border-border">
          <div className="grid grid-cols-7 gap-1">
            <div></div>
            <div className="col-span-5 flex items-center justify-center">
              <h2 className="text-lg font-semibold text-white">
                {t('parameters')}
              </h2>
            </div>
            <div className="flex justify-center">
              <button
                onClick={onClose}
                className="w-8 h-8 text-sm rounded-md transition-colors border border-border text-textSecondary focus:outline-none focus:ring-0 flex items-center justify-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex items-center justify-center py-4">
              <div className="text-white">Загрузка параметров...</div>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center py-4">
              <div className="text-textSecondary text-center">
                {error === "Trading bot is unavailable" ? t('tradingBotUnavailable') : error}
              </div>
            </div>
          )}

          {!loading && !error && localParams && (
            <div className="space-y-4">
              {/* Buy Amount */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('buyAmount')}:
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={inputValues.buy_amount || ''}
                  onChange={(e) => handleInputChange('buy_amount', e.target.value)}
                  className={`w-full p-2 bg-background border rounded text-white focus:outline-none focus:ring-0 input-neon-focus ${
                    validationErrors.buy_amount ? 'border-red-500' : 'border-border'
                  }`}
                  placeholder="1"
                />
                {validationErrors.buy_amount && (
                  <div className="text-red-400 text-xs mt-1">
                    {validationErrors.buy_amount}
                  </div>
                )}
              </div>

              {/* Profit Percent */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('profitPercent')} (%):
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={inputValues.profit_percent || ''}
                  onChange={(e) => handleInputChange('profit_percent', e.target.value)}
                  className={`w-full p-2 bg-background border rounded text-white focus:outline-none focus:ring-0 input-neon-focus ${
                    validationErrors.profit_percent ? 'border-red-500' : 'border-border'
                  }`}
                  placeholder="0.4"
                />
                {validationErrors.profit_percent && (
                  <div className="text-red-400 text-xs mt-1">
                    {validationErrors.profit_percent}
                  </div>
                )}
              </div>

              {/* Auto Buy Up Interval */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('autoBuyUpInterval')} ({t('seconds')}):
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={inputValues.auto_buy_up_interval || ''}
                  onChange={(e) => handleInputChange('auto_buy_up_interval', e.target.value)}
                  className={`w-full p-2 bg-background border rounded text-white focus:outline-none focus:ring-0 input-neon-focus ${
                    validationErrors.auto_buy_up_interval ? 'border-red-500' : 'border-border'
                  }`}
                  placeholder="45"
                />
                {validationErrors.auto_buy_up_interval && (
                  <div className="text-red-400 text-xs mt-1">
                    {validationErrors.auto_buy_up_interval}
                  </div>
                )}
              </div>

              {/* Auto Buy Down Percent */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('autoBuyDownPercent')} (%):
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={inputValues.auto_buy_down_percent || ''}
                  onChange={(e) => handleInputChange('auto_buy_down_percent', e.target.value)}
                  className={`w-full p-2 bg-background border rounded text-white focus:outline-none focus:ring-0 input-neon-focus ${
                    validationErrors.auto_buy_down_percent ? 'border-red-500' : 'border-border'
                  }`}
                  placeholder="1.0"
                />
                {validationErrors.auto_buy_down_percent && (
                  <div className="text-red-400 text-xs mt-1">
                    {validationErrors.auto_buy_down_percent}
                  </div>
                )}
              </div>

              {/* Fee Percent */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('feePercent')} (%):
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={inputValues.fee_percent || ''}
                  onChange={(e) => handleInputChange('fee_percent', e.target.value)}
                  className={`w-full p-2 bg-background border rounded text-white focus:outline-none focus:ring-0 input-neon-focus ${
                    validationErrors.fee_percent ? 'border-red-500' : 'border-border'
                  }`}
                  placeholder="0.025"
                />
                {validationErrors.fee_percent && (
                  <div className="text-red-400 text-xs mt-1">
                    {validationErrors.fee_percent}
                  </div>
                )}
              </div>

              {/* Update Error */}
              {updateError && (
                <div className="text-red-400 text-center text-sm">
                  {updateError}
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3">
                {/* Reset Button */}
                <button
                  onClick={handleResetParameters}
                  disabled={resetting || updating}
                  className="flex-1 p-3 bg-header border border-border text-white rounded-lg transition-colors focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resetting ? t('resetting') : t('resetParameters')}
                </button>

                {/* Update Button */}
                <button
                  onClick={handleUpdateParameters}
                  disabled={updating || resetting || Object.keys(validationErrors).length > 0}
                  className="flex-1 p-3 bg-header border border-border text-white rounded-lg transition-colors focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updating ? t('updating') : t('updateParameters')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 